import * as React from "react";

import { cn } from "./utils";

/**
 * Composition-safe controlled input.
 *
 * On mobile, IME composition events (e.g. iOS autocorrecting "--" into "—")
 * race with React's controlled-value reconciliation.  During composition React
 * may force the DOM value back to the stale controlled `value` prop, which
 * drops characters or swallows special characters entirely.
 *
 * This wrapper buffers the value in local state during an active composition
 * and defers the parent `onChange` until `compositionEnd`, so the browser can
 * finish its replacement uninterrupted.  Outside of composition the component
 * behaves identically to a plain `<input>`.
 */
const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  (
    {
      className,
      type,
      value,
      defaultValue,
      onChange,
      onCompositionStart,
      onCompositionEnd,
      ...props
    },
    ref,
  ) => {
    const isControlled = value !== undefined;
    const composingRef = React.useRef(false);
    // One-shot flag: after compositionEnd flushes onChange, the very next
    // native input event (Chrome/Safari fire it *after* compositionend)
    // must be suppressed to avoid a double-fire.
    const flushedRef = React.useRef(false);

    // Internal mirror so the DOM stays in sync during IME composition even
    // though we defer the parent onChange.
    const [buf, setBuf] = React.useState<
      string | number | readonly string[] | undefined
    >(() => (isControlled ? value : defaultValue));

    // Keep buf in sync with the parent-controlled value — but never overwrite
    // what the user is actively composing.
    if (isControlled && !composingRef.current && value !== buf) {
      setBuf(value);
    }

    return (
      <input
        type={type}
        data-slot="input"
        className={cn(
          "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-9 w-full min-w-0 rounded-md border px-3 py-1 text-base bg-input-background transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
          "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
          className,
        )}
        ref={ref}
        {...(isControlled ? { value: buf } : { defaultValue })}
        onChange={(e) => {
          setBuf(e.target.value);
          // On Chrome/Safari compositionend fires before the trailing
          // native input event.  flushedRef prevents double-firing.
          if (flushedRef.current) {
            flushedRef.current = false;
            return;
          }
          if (!composingRef.current) {
            onChange?.(e);
          }
        }}
        onCompositionStart={(e) => {
          composingRef.current = true;
          onCompositionStart?.(e);
        }}
        onCompositionEnd={(e) => {
          composingRef.current = false;
          flushedRef.current = true;
          // On Firefox compositionend fires *after* the last input event, so
          // no trailing input event follows to clear flushedRef.  A setTimeout
          // clears the flag after the current event cycle on all browsers.
          // Chrome/Safari: trailing input clears it first; this is a no-op.
          // Firefox: no trailing input; this prevents the next real keystroke
          // from being swallowed.
          setTimeout(() => {
            flushedRef.current = false;
          }, 0);
          const el = e.currentTarget;
          setBuf(el.value);
          if (onChange) {
            onChange({
              target: el,
              currentTarget: el,
            } as React.ChangeEvent<HTMLInputElement>);
          }
          onCompositionEnd?.(e);
        }}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

export { Input };
